import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Alert,
  CircularProgress,
  Box,
  Container,
  Card,
  CardContent,
  Avatar,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { astrologerRegistrationService } from '../../../services/api';
import { useRegistration } from '../../../context/RegistrationContext';

const TokenVerifier = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setToken, setEmail } = useRegistration();
  const [status, setStatus] = useState('loading');
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await astrologerRegistrationService.verifyInvitation(token);

        if (response.data.valid) {
          setStatus('valid');
          setInvitationData(response.data);
          setToken(token);
          setEmail(response.data.email);
        } else {
          setStatus('invalid');
          setError('This invitation link is invalid or has expired.');
        }
      } catch (error) {
        setStatus('invalid');
        if (error.response?.status === 404) {
          setError('Invitation not found. Please check your link.');
        } else if (error.response?.status === 400) {
          setError('This invitation has expired or has already been used.');
        } else {
          setError('An error occurred while verifying your invitation.');
        }
      }
    };

    if (token) {
      verifyToken();
    } else {
      setStatus('invalid');
      setError('Invalid invitation link.');
    }
  }, [token, setToken, setEmail]);

  const handleContinue = () => {
    navigate('/create-password', {
      state: {
        token: token,
        email: invitationData?.email,
        profile: {
          firstName: invitationData?.firstName || '',
          lastName: invitationData?.lastName || '',
          profileImageUrl: invitationData?.profileImageUrl || '',
          bio: invitationData?.bio || '',
          specialization: invitationData?.specialization || '',
          experienceYears: invitationData?.experienceYears || 0,
          pricePerMinuteChat: invitationData?.pricePerMinuteChat || 0,
          pricePerMinuteCall: invitationData?.pricePerMinuteCall || 0,
          pricePerMinuteVideo: invitationData?.pricePerMinuteVideo || 0,
        }
      }
    });
  };

  const fullName = invitationData
    ? [invitationData.firstName, invitationData.lastName].filter(Boolean).join(' ')
    : '';

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
          Astrologer Invitation
        </Typography>

        <Card sx={{ width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {status === 'loading' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress size={48} />
                <Typography variant="h6" sx={{ mt: 3 }}>
                  Verifying your invitation...
                </Typography>
              </Box>
            )}

            {status === 'invalid' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h5" gutterBottom color="error.main">
                  Invalid Invitation
                </Typography>
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
                <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                  If you believe this is an error, please contact our support team.
                </Typography>
              </Box>
            )}

            {status === 'valid' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h5" gutterBottom color="success.main">
                  Invitation Verified!
                </Typography>

                <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                  Your invitation is valid and ready to use.
                </Alert>

                {/* Profile Preview */}
                {fullName && (
                  <Box sx={{ width: '100%', mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={invitationData.profileImageUrl || ''}
                      sx={{ width: 64, height: 64, bgcolor: '#1976d2' }}
                    >
                      {invitationData.firstName?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitationData.specialization || 'Astrologer'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Rates */}
                {(invitationData.pricePerMinuteChat > 0 || invitationData.pricePerMinuteCall > 0) && (
                  <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: '100%', mb: 2 }}>
                    {invitationData.pricePerMinuteChat > 0 && (
                      <Chip label={`Chat: ₹${invitationData.pricePerMinuteChat}/min`} size="small" variant="outlined" />
                    )}
                    {invitationData.pricePerMinuteCall > 0 && (
                      <Chip label={`Call: ₹${invitationData.pricePerMinuteCall}/min`} size="small" variant="outlined" />
                    )}
                    {invitationData.pricePerMinuteVideo > 0 && (
                      <Chip label={`Video: ₹${invitationData.pricePerMinuteVideo}/min`} size="small" variant="outlined" />
                    )}
                  </Box>
                )}

                <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, width: '100%', my: 1 }}>
                  <Typography variant="body2">
                    <strong>Email:</strong> {invitationData.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expires:</strong> {new Date(invitationData.expiresAt).toLocaleDateString()}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleContinue}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  Continue to Registration
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default TokenVerifier;
